import React, { useState, useEffect, useCallback } from 'react';
import { AnalysisStats as AnalysisStatsData } from '../services/projectService';
import {
  getComprehensiveDashboardStats,
  ComprehensiveDashboardStats,
  RiskDistributionStats,
  RiskMatrixStats,
  WorkflowProgressStats
} from '../services/resultService';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { FaChartPie, FaTh, FaTasks, FaChartBar, FaTimes } from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner';
import './AnalysisStats.css';
import './Charts.css';

interface AnalysisStatsProps {
  stats: AnalysisStatsData | null;
  isLoading: boolean;
  projectId: string;
}

/* ================================================================
   Main component — compact chart tiles + expand-on-click overlay
   ================================================================ */
const AnalysisStats: React.FC<AnalysisStatsProps> = ({ stats, isLoading, projectId }) => {
  const [dashboardStats, setDashboardStats] = useState<ComprehensiveDashboardStats | null>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [expandedChart, setExpandedChart] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (!projectId) return;
      setIsLoadingDashboard(true);
      try {
        const data = await getComprehensiveDashboardStats(projectId);
        setDashboardStats(data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setIsLoadingDashboard(false);
      }
    };
    fetchDashboardStats();
  }, [projectId]);

  // Close on Escape key
  useEffect(() => {
    if (!expandedChart) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpandedChart(null); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expandedChart]);

  const closeOverlay = useCallback(() => setExpandedChart(null), []);

  if (isLoading) {
    return <div className="stats-container loading">Loading stats...</div>;
  }
  if (!stats) {
    return <div className="stats-container empty">No stats available.</div>;
  }

  // Applicability data
  const applicabilityData = [
    { name: 'Applicable', value: stats.applicable_items, color: '#10b981' },
    { name: 'Not Applicable', value: stats.total_items - stats.applicable_items, color: '#ef4444' }
  ].filter(item => item.value > 0);

  return (
    <div className="analytics-dashboard">
      {/* Compact tile row */}
      <div className="charts-tile-row">
        <ChartTile
          id="workflow"
          icon={<FaTasks />}
          title="Workflow"
          subtitle={`${dashboardStats?.workflow_progress?.total_items ?? 0} items`}
          onClick={() => setExpandedChart('workflow')}
        >
          <WorkflowMini data={dashboardStats?.workflow_progress || null} isLoading={isLoadingDashboard} />
        </ChartTile>

        <ChartTile
          id="applicability"
          icon={<FaChartPie />}
          title="Applicability"
          subtitle={`${stats.total_items} analyzed`}
          onClick={() => setExpandedChart('applicability')}
        >
          <ApplicabilityMini data={applicabilityData} />
        </ChartTile>

        <ChartTile
          id="risk-dist"
          icon={<FaChartBar />}
          title="Risk Distribution"
          subtitle={`${dashboardStats?.risk_distribution?.total_assessed_items ?? 0} assessed`}
          onClick={() => setExpandedChart('risk-dist')}
        >
          <RiskDistributionMini data={dashboardStats?.risk_distribution || null} isLoading={isLoadingDashboard} />
        </ChartTile>

        <ChartTile
          id="risk-matrix"
          icon={<FaTh />}
          title="Risk Matrix"
          subtitle={`${dashboardStats?.risk_matrix?.total_assessed_items ?? 0} plotted`}
          onClick={() => setExpandedChart('risk-matrix')}
        >
          <RiskMatrixMini data={dashboardStats?.risk_matrix || null} isLoading={isLoadingDashboard} />
        </ChartTile>
      </div>

      {/* Expanded overlay */}
      {expandedChart && (
        <div className="chart-overlay" onClick={closeOverlay}>
          <div className="chart-overlay__card" onClick={(e) => e.stopPropagation()}>
            <button className="chart-overlay__close" onClick={closeOverlay}><FaTimes /></button>

            {expandedChart === 'workflow' && (
              <WorkflowProgressChart data={dashboardStats?.workflow_progress || null} isLoading={isLoadingDashboard} />
            )}
            {expandedChart === 'applicability' && (
              <ApplicabilityChart data={applicabilityData} totalItems={stats.total_items} />
            )}
            {expandedChart === 'risk-dist' && (
              <RiskDistributionChart data={dashboardStats?.risk_distribution || null} isLoading={isLoadingDashboard} />
            )}
            {expandedChart === 'risk-matrix' && (
              <RiskMatrixChart data={dashboardStats?.risk_matrix || null} isLoading={isLoadingDashboard} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   ChartTile — compact clickable card
   ================================================================ */
interface ChartTileProps {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  children: React.ReactNode;
}

const ChartTile: React.FC<ChartTileProps> = ({ icon, title, subtitle, onClick, children }) => (
  <div className="chart-tile" onClick={onClick} role="button" tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
    <div className="chart-tile__header">
      <span className="chart-tile__icon">{icon}</span>
      <div className="chart-tile__text">
        <span className="chart-tile__title">{title}</span>
        <span className="chart-tile__subtitle">{subtitle}</span>
      </div>
    </div>
    <div className="chart-tile__preview">{children}</div>
    <span className="chart-tile__hint">Click to expand</span>
  </div>
);

/* ================================================================
   Mini chart previews (thumbnails inside tiles)
   ================================================================ */
const WorkflowMini: React.FC<{ data: WorkflowProgressStats | null; isLoading: boolean }> = ({ data, isLoading }) => {
  if (isLoading) return <div className="chart-tile__loading"></div>;
  if (!data || data.total_items === 0) return <div className="chart-tile__empty">No data</div>;

  const segments = [
    { label: 'Done', value: data.completed, color: '#10b981' },
    { label: 'Review', value: data.pending_applicability_review, color: '#f59e0b' },
    { label: 'Risk', value: data.pending_risk_assessment, color: '#3b82f6' },
    { label: 'Analysis', value: data.pending_ai_analysis, color: '#fbbf24' },
    { label: 'Failed', value: data.failed, color: '#ef4444' },
  ].filter(s => s.value > 0);

  return (
    <div className="mini-bar">
      {segments.map((s) => (
        <div key={s.label} className="mini-bar__seg" style={{
          width: `${(s.value / data.total_items) * 100}%`,
          background: s.color
        }} title={`${s.label}: ${s.value}`}></div>
      ))}
    </div>
  );
};

const ApplicabilityMini: React.FC<{ data: { name: string; value: number; color: string }[] }> = ({ data }) => {
  if (data.length === 0) return <div className="chart-tile__empty">No data</div>;
  const total = data.reduce((a, d) => a + d.value, 0);
  return (
    <div className="mini-donut-row">
      {data.map(d => (
        <div key={d.name} className="mini-donut-item">
          <span className="mini-donut-dot" style={{ background: d.color }}></span>
          <span className="mini-donut-pct">{total > 0 ? Math.round((d.value / total) * 100) : 0}%</span>
        </div>
      ))}
    </div>
  );
};

const RiskDistributionMini: React.FC<{ data: RiskDistributionStats | null; isLoading: boolean }> = ({ data, isLoading }) => {
  if (isLoading) return <div className="chart-tile__loading"></div>;
  if (!data || data.total_assessed_items === 0) return <div className="chart-tile__empty">No data</div>;

  const colors: Record<string, string> = { 'Bajo': '#10b981', 'Moderado': '#f59e0b', 'Alto': '#ef4444', 'Extremo': '#7c2d12' };
  return (
    <div className="mini-bar">
      {data.risk_levels.map((l) => (
        <div key={l.risk_level} className="mini-bar__seg" style={{
          width: `${l.percentage}%`,
          background: colors[l.risk_level] || '#6b7280'
        }} title={`${l.risk_level}: ${l.count}`}></div>
      ))}
    </div>
  );
};

const RiskMatrixMini: React.FC<{ data: RiskMatrixStats | null; isLoading: boolean }> = ({ data, isLoading }) => {
  if (isLoading) return <div className="chart-tile__loading"></div>;
  if (!data || data.total_assessed_items === 0) return <div className="chart-tile__empty">No data</div>;

  const grid: number[][] = Array(5).fill(null).map(() => Array(5).fill(0));
  data.matrix_cells.forEach(cell => {
    if (cell.probability >= 1 && cell.probability <= 5 && cell.impact >= 1 && cell.impact <= 5) {
      grid[5 - cell.impact][cell.probability - 1] = cell.count;
    }
  });
  const max = data.max_cell_count || 1;

  return (
    <div className="mini-matrix">
      {grid.map((row, ri) => row.map((count, ci) => (
        <div key={`${ri}-${ci}`} className="mini-matrix__cell" style={{
          background: count === 0 ? '#f3f4f6' : `rgba(239, 68, 68, ${Math.min(0.2 + (count / max) * 0.8, 1)})`
        }}></div>
      )))}
    </div>
  );
};

/* ================================================================
   Full-size chart components (shown inside the overlay)
   ================================================================ */

// Applicability (full)
const ApplicabilityChart: React.FC<{ data: { name: string; value: number; color: string }[]; totalItems: number }> = ({ data, totalItems }) => (
  <div className="chart-full">
    <div className="chart-full__header">
      <h3>Applicability Breakdown</h3>
      <p className="chart-full__sub">{totalItems} total items analyzed</p>
    </div>
    <ResponsiveContainer width="100%" height={340}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" labelLine={false}
          label={({ percent }) => percent && percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
          outerRadius={120} fill="#8884d8" dataKey="value">
          {data.map((entry, i) => <Cell key={`c-${i}`} fill={entry.color} />)}
        </Pie>
        <Tooltip formatter={(value: number, name: string) => [value, `${name} Items`]} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  </div>
);

// Risk Distribution (full)
const RiskDistributionChart: React.FC<{ data: RiskDistributionStats | null; isLoading: boolean }> = ({ data, isLoading }) => {
  if (isLoading) return <LoadingSpinner />;
  if (!data || data.total_assessed_items === 0) return <div className="chart-no-data"><p>No risk assessments completed yet.</p></div>;

  const colors: Record<string, string> = { 'Bajo': '#10b981', 'Moderado': '#f59e0b', 'Alto': '#ef4444', 'Extremo': '#7c2d12' };
  const chartData = data.risk_levels.map(item => ({ name: item.risk_level, value: item.count, percentage: item.percentage, color: colors[item.risk_level] || '#6b7280' }));

  return (
    <div className="chart-full">
      <div className="chart-full__header">
        <h3>Risk Level Distribution</h3>
        <p className="chart-full__sub">{data.total_assessed_items} items assessed</p>
      </div>
      <ResponsiveContainer width="100%" height={340}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" labelLine={false}
            label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
              if (percent < 0.05) return null;
              const RADIAN = Math.PI / 180;
              const r = innerRadius + (outerRadius - innerRadius) * 0.5;
              const x = cx + r * Math.cos(-midAngle * RADIAN);
              const y = cy + r * Math.sin(-midAngle * RADIAN);
              return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize="12" fontWeight="bold">{`${(percent * 100).toFixed(0)}%`}</text>;
            }}
            outerRadius={120} fill="#8884d8" dataKey="value">
            {chartData.map((entry, i) => <Cell key={`c-${i}`} fill={entry.color} />)}
          </Pie>
          <Tooltip formatter={(value: number, _name: string, props: any) => [`${value} (${props.payload.percentage.toFixed(1)}%)`, props.payload.name]} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Risk Matrix (full)
const RiskMatrixChart: React.FC<{ data: RiskMatrixStats | null; isLoading: boolean }> = ({ data, isLoading }) => {
  if (isLoading) return <LoadingSpinner />;
  if (!data || data.total_assessed_items === 0) return <div className="chart-no-data"><p>No risk assessments completed yet.</p></div>;

  const grid: Array<Array<{ count: number; items: number[] }>> = Array(5).fill(null).map(() => Array(5).fill(null).map(() => ({ count: 0, items: [] })));
  data.matrix_cells.forEach(cell => {
    if (cell.probability >= 1 && cell.probability <= 5 && cell.impact >= 1 && cell.impact <= 5) {
      grid[5 - cell.impact][cell.probability - 1] = { count: cell.count, items: cell.items };
    }
  });
  const max = data.max_cell_count || 1;
  const getCellColor = (count: number) => {
    if (count === 0) return '#f9fafb';
    const i = count / max;
    return `rgb(${Math.floor(255 * i)}, ${Math.floor(255 * (1 - i * 0.7))}, ${Math.floor(255 * (1 - i * 0.7))})`;
  };

  return (
    <div className="chart-full">
      <div className="chart-full__header">
        <h3>Risk Matrix (5x5)</h3>
        <p className="chart-full__sub">{data.total_assessed_items} items plotted</p>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <svg viewBox="0 0 500 380" className="risk-matrix-svg">
          <defs><style>{`.risk-matrix-cell{stroke:#e5e7eb;stroke-width:1}.risk-matrix-text{font-family:system-ui;font-size:12px;fill:#1f2937}.risk-matrix-axis-label{font-family:system-ui;font-size:14px;fill:#374151;font-weight:600}.risk-matrix-axis-tick{font-family:system-ui;font-size:11px;fill:#6b7280}`}</style></defs>
          {grid.map((row, ri) => row.map((cell, ci) => (
            <g key={`${ri}-${ci}`}>
              <rect x={50 + ci * 80} y={50 + ri * 50} width={80} height={50} fill={getCellColor(cell.count)} className="risk-matrix-cell" />
              {cell.count > 0 && <text x={50 + ci * 80 + 40} y={50 + ri * 50 + 30} textAnchor="middle" className="risk-matrix-text" fontWeight="bold">{cell.count}</text>}
            </g>
          )))}
          {[5,4,3,2,1].map((v, i) => <text key={`y-${v}`} x="25" y={50 + i * 50 + 30} textAnchor="middle" className="risk-matrix-axis-tick">{v}</text>)}
          {[1,2,3,4,5].map((v, i) => <text key={`x-${v}`} x={50 + i * 80 + 40} y={320} textAnchor="middle" className="risk-matrix-axis-tick">{v}</text>)}
          <text x="15" y="175" textAnchor="middle" className="risk-matrix-axis-label" transform="rotate(-90, 15, 175)">Impact</text>
          <text x="300" y="370" textAnchor="middle" className="risk-matrix-axis-label">Probability</text>
        </svg>
      </ResponsiveContainer>
    </div>
  );
};

// Workflow Progress (full)
const WorkflowProgressChart: React.FC<{ data: WorkflowProgressStats | null; isLoading: boolean }> = ({ data, isLoading }) => {
  if (isLoading) return <LoadingSpinner />;
  if (!data || data.total_items === 0) return <div className="chart-no-data"><p>No analysis items found.</p></div>;

  const barData = [
    { name: 'Pending AI', count: data.pending_ai_analysis, color: '#fbbf24' },
    { name: 'Pending Review', count: data.pending_applicability_review, color: '#f59e0b' },
    { name: 'Pending Risk', count: data.pending_risk_assessment, color: '#3b82f6' },
    { name: 'Completed', count: data.completed, color: '#10b981' },
    { name: 'Failed', count: data.failed, color: '#ef4444' },
  ].filter(item => item.count > 0);

  return (
    <div className="chart-full">
      <div className="chart-full__header">
        <h3>Workflow Progress</h3>
        <p className="chart-full__sub">{data.total_items} total items</p>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {barData.map((entry, i) => <Cell key={`c-${i}`} fill={entry.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AnalysisStats;
